/**
 * LazyLoader utility for implementing lazy loading of images and content
 * Uses Intersection Observer API for optimal performance
 */

interface LazyLoadOptions {
  rootMargin?: string | undefined;
  threshold?: number | undefined;
  loadingClass?: string | undefined;
  loadedClass?: string | undefined;
  errorClass?: string | undefined;
}

interface LazyLoadElement extends HTMLElement {
  dataset: {
    src?: string;
    srcset?: string;
    lazy?: string;
    [key: string]: string | undefined;
  };
}

export class LazyLoader {
  private observer: IntersectionObserver | null = null;
  private options: LazyLoadOptions;
  private loadedElements = new Set<Element>();

  constructor(options: LazyLoadOptions = {}) {
    this.options = {
      rootMargin: '50px 0px',
      threshold: 0.01,
      loadingClass: 'lazy-loading',
      loadedClass: 'lazy-loaded',
      errorClass: 'lazy-error',
      ...options
    };

    this.init();
  }

  /**
   * Initialize the lazy loader
   */
  private init(): void {
    if (!this.isIntersectionObserverSupported()) {
      // Fallback: load all images immediately
      this.loadAllImages();
      return;
    }

    this.createObserver();
    this.observeElements();
  }

  /**
   * Check if Intersection Observer is supported
   */
  private isIntersectionObserverSupported(): boolean {
    return 'IntersectionObserver' in window;
  }

  /**
   * Create the Intersection Observer
   */
  private createObserver(): void {
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        rootMargin: this.options.rootMargin!,
        threshold: this.options.threshold!
      }
    );
  }

  /**
   * Handle intersection events
   */
  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.loadedElements.has(entry.target)) {
        this.loadElement(entry.target as LazyLoadElement);
        this.observer?.unobserve(entry.target);
      }
    });
  }

  /**
   * Load a specific element
   */
  private loadElement(element: LazyLoadElement): void {
    if (this.loadedElements.has(element)) {
      return;
    }

    this.loadedElements.add(element);
    element.classList.add(this.options.loadingClass!);

    if (element.tagName === 'IMG') {
      this.loadImage(element as HTMLImageElement);
    } else if (element.dataset.lazy === 'background') {
      this.loadBackgroundImage(element);
    } else {
      this.loadContent(element);
    }
  }

  /**
   * Load an image element
   */
  private loadImage(img: HTMLImageElement & LazyLoadElement): void {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;

    if (!src && !srcset) {
      this.handleError(img);
      return;
    }

    // Create a new image to preload
    const imageLoader = new Image();
    
    imageLoader.onload = () => {
      // Set the actual src and srcset
      if (src) img.src = src;
      if (srcset) img.srcset = srcset;
      
      this.handleSuccess(img);
    };

    imageLoader.onerror = () => {
      this.handleError(img);
    };

    // Start loading
    if (src) imageLoader.src = src;
  }

  /**
   * Load a background image
   */
  private loadBackgroundImage(element: LazyLoadElement): void {
    const src = element.dataset.src;
    
    if (!src) {
      this.handleError(element);
      return;
    }

    // Create a new image to preload
    const imageLoader = new Image();
    
    imageLoader.onload = () => {
      element.style.backgroundImage = `url(${src})`;
      this.handleSuccess(element);
    };

    imageLoader.onerror = () => {
      this.handleError(element);
    };

    imageLoader.src = src;
  }

  /**
   * Load content (for content sections)
   */
  private loadContent(element: LazyLoadElement): void {
    // For content sections, just mark as loaded
    // This can be extended for dynamic content loading
    this.handleSuccess(element);
  }

  /**
   * Handle successful loading
   */
  private handleSuccess(element: Element): void {
    element.classList.remove(this.options.loadingClass!);
    element.classList.add(this.options.loadedClass!);
    
    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('lazyloaded', {
      bubbles: true,
      detail: { element }
    }));
  }

  /**
   * Handle loading error
   */
  private handleError(element: Element): void {
    element.classList.remove(this.options.loadingClass!);
    element.classList.add(this.options.errorClass!);
    
    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('lazyerror', {
      bubbles: true,
      detail: { element }
    }));
  }

  /**
   * Observe all lazy elements on the page
   */
  public observeElements(): void {
    if (!this.observer) return;

    const lazyElements = document.querySelectorAll('[data-lazy]');
    lazyElements.forEach(element => {
      this.observer!.observe(element);
    });
  }

  /**
   * Add new elements to observe
   */
  public observe(element: Element): void {
    if (this.observer && !this.loadedElements.has(element)) {
      this.observer.observe(element);
    }
  }

  /**
   * Stop observing an element
   */
  public unobserve(element: Element): void {
    if (this.observer) {
      this.observer.unobserve(element);
    }
  }

  /**
   * Load all images immediately (fallback)
   */
  private loadAllImages(): void {
    const lazyElements = document.querySelectorAll('[data-lazy]') as NodeListOf<LazyLoadElement>;
    lazyElements.forEach(element => {
      this.loadElement(element);
    });
  }

  /**
   * Refresh - observe new elements that might have been added
   */
  public refresh(): void {
    this.observeElements();
  }

  /**
   * Destroy the lazy loader
   */
  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.loadedElements.clear();
  }

  /**
   * Static method to initialize lazy loading on page load
   */
  static init(options?: LazyLoadOptions): LazyLoader {
    return new LazyLoader(options);
  }
}

// Auto-initialize if DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    LazyLoader.init();
  });
} else {
  LazyLoader.init();
}